import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../lib/backend';
import { useAuth } from '../context/AuthContext';

const ACCESS_CODE = 't4t2026';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ComingSoon() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // Waitlist state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(null); // 'artist' | 'fan' | null
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [artistName, setArtistName] = useState('');
  const artistNameRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem('t4t_access')) {
      navigate('/home', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === ACCESS_CODE) {
      sessionStorage.setItem('t4t_access', 'true');
      navigate('/home', { replace: true });
    } else {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setTimeout(() => setError(false), 3000);
      setCode('');
    }
  };

  const submitWaitlist = async (name) => {
    setSubmitting(true);
    setFormError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim(),
          role,
          ...(name ? { artist_name: name.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not join the waitlist. Please try again.');
      }
      const data = await res.json().catch(() => ({}));
      // Unlock the site for early-access browsing
      sessionStorage.setItem('t4t_access', 'true');
      if (data.demo_login) {
        // Artist signups are auto-logged into the shared Demo Artist account
        // so they can explore everything an artist can do (read-only).
        await refreshUser();
      }
      navigate('/home', { replace: true });
    } catch (err) {
      setShowArtistModal(false);
      setFormError(typeof err.message === 'string' ? err.message : 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWaitlist = (e) => {
    e.preventDefault();
    setFormError('');
    if (!EMAIL_RE.test(email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!role) {
      setFormError('Please select whether you are an Artist or a Fan.');
      return;
    }
    if (role === 'artist') {
      // Ask for the artist name first
      setShowArtistModal(true);
      setTimeout(() => artistNameRef.current?.focus(), 50);
      return;
    }
    submitWaitlist();
  };

  const handleArtistNameSubmit = (e) => {
    e.preventDefault();
    if (!artistName.trim()) return;
    submitWaitlist(artistName);
  };

  return (
    <div
      className="min-h-screen w-full overflow-y-auto"
      style={{ background: '#050505' }}
      data-testid="coming-soon-page"
    >
      <img
        src="/coming-soon-bg-v2.jpg"
        alt=""
        className="fixed inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 pt-10 pb-32">
        {/* Logo */}
        <img
          src="/tune4tune-logo-v2.png"
          alt="Tune4Tune"
          className="w-[260px] sm:w-[320px] md:w-[380px] mb-10 select-none drop-shadow-[0_0_30px_rgba(205,220,57,0.15)]"
          draggable={false}
          data-testid="coming-soon-logo"
        />

        {/* Waitlist panel */}
        <div
          className="w-full max-w-md rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md p-6 shadow-2xl"
          data-testid="waitlist-panel"
        >
          <>
              <h2 className="text-[#CDDC39] text-lg font-semibold tracking-wide mb-1">
                Get Early Access
              </h2>
              <p className="text-white/60 text-xs mb-4">
                Be the first to know when Tune4Tune launches.
              </p>

              <form onSubmit={handleWaitlist} className="space-y-3" noValidate>
                <div>
                  <label className="block text-white/70 text-[11px] uppercase tracking-wider mb-1">
                    Enter your email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFormError(''); }}
                    placeholder="yourname@email.com"
                    className="w-full px-3 py-2 text-sm bg-black/50 border border-white/10 text-white/90 placeholder-white/30 rounded-lg outline-none focus:border-[#CDDC39]/40 transition-colors"
                    data-testid="waitlist-email-input"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-[11px] uppercase tracking-wider mb-1">
                    I am an
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { setRole('artist'); setFormError(''); }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                        role === 'artist'
                          ? 'bg-[#CDDC39] text-black border-[#CDDC39] font-semibold'
                          : 'bg-black/30 text-white/70 border-white/10 hover:border-white/30'
                      }`}
                      data-testid="role-artist-btn"
                    >
                      Artist
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRole('fan'); setFormError(''); }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                        role === 'fan'
                          ? 'bg-[#CDDC39] text-black border-[#CDDC39] font-semibold'
                          : 'bg-black/30 text-white/70 border-white/10 hover:border-white/30'
                      }`}
                      data-testid="role-fan-btn"
                    >
                      User / Fan
                    </button>
                  </div>
                </div>

                {formError && (
                  <p className="text-xs text-red-400/90" data-testid="waitlist-error">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-4 py-2.5 text-sm font-semibold rounded-lg bg-[#CDDC39] text-black hover:bg-[#bcc933] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="waitlist-submit-btn"
                >
                  {submitting ? 'Entering…' : 'Enter'}
                </button>
              </form>
          </>
        </div>
      </div>

      {/* Artist name popup */}
      {showArtistModal && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          data-testid="artist-name-modal"
        >
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-black/80 backdrop-blur-md p-6 shadow-2xl">
            <h3 className="text-[#CDDC39] text-lg font-semibold tracking-wide mb-1">
              What&apos;s your Artist Name?
            </h3>
            <p className="text-white/60 text-xs mb-4">
              Tell us the name you perform under so we can look out for you.
            </p>
            <form onSubmit={handleArtistNameSubmit} className="space-y-3" noValidate>
              <input
                ref={artistNameRef}
                type="text"
                value={artistName}
                maxLength={100}
                onChange={(e) => setArtistName(e.target.value)}
                placeholder="e.g. Lion Vibes"
                className="w-full px-3 py-2 text-sm bg-black/50 border border-white/10 text-white/90 placeholder-white/30 rounded-lg outline-none focus:border-[#CDDC39]/40 transition-colors"
                data-testid="artist-name-input"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowArtistModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg bg-black/40 text-white/70 border border-white/10 hover:border-white/30 transition-colors"
                  data-testid="artist-name-cancel-btn"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting || !artistName.trim()}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg bg-[#CDDC39] text-black hover:bg-[#bcc933] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="artist-name-submit-btn"
                >
                  {submitting ? 'Entering…' : 'Enter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Access code — pinned to bottom */}
      <div
        className="fixed bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        onClick={() => inputRef.current?.focus()}
      >
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="password"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(false); }}
            placeholder="enter access code"
            autoComplete="off"
            spellCheck={false}
            className={`
              w-52 px-4 py-2 text-center text-sm tracking-widest
              bg-black/60 backdrop-blur-sm border border-white/10
              text-[#CDDC39] placeholder-white/30
              rounded-full outline-none
              focus:border-[#CDDC39]/30
              transition-all
              ${shaking ? 'animate-shake' : ''}
            `}
            data-testid="access-code-input"
          />
        </form>
        {error && (
          <p className="text-xs text-red-400/90 tracking-wide" data-testid="access-code-error">
            Invalid access code
          </p>
        )}
      </div>
    </div>
  );
}
